import { useState, type ReactNode } from "react";
import { Card, StatusIndicator, type StatusTone } from "./ui";
import { LabeledField, FindingList, NO_DISCORD_CHANGES, HUMAN_APPROVAL_REQUIRED, type ContentFinding } from "./onboarding";
import {
  SCHEDULE_KINDS,
  SUGGESTION_CLOSE_STATUSES,
  type AnnouncementRow,
  type EngagementModules,
  type EngagementSchedule,
  type EngagementStatistics,
  type EventRow,
  type PollOption,
  type PollWithTally,
  type ReminderRow,
  type ScheduleKind,
  type SuggestionStatus,
  type SuggestionWithTally,
  type TicketRow,
  type TicketStatus,
} from "../lib/api";
import { formatDateTime } from "../lib/format";

/**
 * Presentational + pure building blocks for the Phase 6C engagement admin
 * surfaces (tickets, suggestions, polls, scheduled content, statistics). Every
 * component here is deliberately data-fetch-free so it can be unit tested with
 * mock data, and every surface is honest about what it does.
 *
 * SAFETY LANGUAGE (used across these surfaces):
 *  - Config drafts / validation: "No Discord changes have been made."
 *  - Ticket channel creation + message posting run through the LOCKED engagement
 *    boundary (records QUEUED, no live Discord effect by default).
 *  - Config activation is a hand-off to the EXISTING Phase 4 approval pipeline —
 *    there is deliberately no bypass "deploy" button here.
 *  - Every count / tally shown is a real database value — nothing is fabricated.
 */
export const LOCKED_TICKET_NOTE =
  "Ticket channel creation runs through the approval-bound, locked engagement pipeline. Claiming, closing and archiving update ODIN records — no live Discord channel is created or edited by default.";
export const LOCKED_POST_NOTE =
  "Announcement, reminder and event posting run through the locked engagement pipeline on schedule — this dashboard never posts to Discord directly. There is no \"post now\" button that acts live.";
export const REAL_COUNTS_NOTE = "Every count and tally below is a real database value recorded from member votes and records — nothing is estimated or fabricated.";

// ── Deterministic schedule math (mirrors @odin/engagement schedule.ts) ────────
const DAY = 86_400;
const WEEK = 7 * DAY;

/** Next fire strictly after `nowEpochSec`, or null when there is no future fire. */
export function computeNextFire(schedule: EngagementSchedule, nowEpochSec: number): number | null {
  switch (schedule.kind) {
    case "one-shot":
      return schedule.atEpochSec > nowEpochSec ? schedule.atEpochSec : null;
    case "interval": {
      if (schedule.everySec <= 0) return null;
      const elapsed = nowEpochSec - schedule.anchorEpochSec;
      const periods = Math.floor(elapsed / schedule.everySec) + 1;
      return schedule.anchorEpochSec + periods * schedule.everySec;
    }
    case "daily": {
      const dayStart = Math.floor(nowEpochSec / DAY) * DAY;
      const today = dayStart + schedule.secondOfDayUtc;
      return today > nowEpochSec ? today : today + DAY;
    }
    case "weekly": {
      const weekStart = Math.floor(nowEpochSec / WEEK) * WEEK;
      for (let w = 0; w <= 1; w += 1) {
        const base = weekStart + w * WEEK;
        const candidate = base + schedule.dayOfWeekUtc * DAY + schedule.secondOfDayUtc;
        if (candidate > nowEpochSec) return candidate;
      }
      return weekStart + WEEK + schedule.dayOfWeekUtc * DAY + schedule.secondOfDayUtc;
    }
  }
}

/** Human-readable next-fire preview for a schedule. */
export function nextFireLabel(schedule: EngagementSchedule, now: number = Date.now()): string {
  const next = computeNextFire(schedule, Math.floor(now / 1000));
  if (next === null) return "No future fire — this schedule will not run again.";
  return `Next fire: ${formatDateTime(new Date(next * 1000))}`;
}

/** One-line description of a schedule's cadence. */
export function describeSchedule(schedule: EngagementSchedule): string {
  switch (schedule.kind) {
    case "one-shot":
      return `Once, at ${formatDateTime(new Date(schedule.atEpochSec * 1000))}`;
    case "interval":
      return `Every ${schedule.everySec} second(s)`;
    case "daily":
      return `Daily at ${secondsToTime(schedule.secondOfDayUtc)} UTC`;
    case "weekly":
      return `Weekly on day ${schedule.dayOfWeekUtc} at ${secondsToTime(schedule.secondOfDayUtc)} UTC`;
  }
}

export function secondsToTime(sec: number): string {
  const h = Math.floor(sec / 3600) % 24;
  const m = Math.floor((sec % 3600) / 60);
  return `${String(h).padStart(2, "0")}:${String(m).padStart(2, "0")}`;
}
export function timeToSeconds(value: string): number {
  const parts = value.split(":").map((n) => Number(n));
  const h = parts[0] ?? 0;
  const m = parts[1] ?? 0;
  if (!Number.isFinite(h) || !Number.isFinite(m)) return 0;
  return (h % 24) * 3600 + (m % 60) * 60;
}
function toDatetimeLocal(epochSec: number): string {
  const d = new Date(epochSec * 1000);
  if (Number.isNaN(d.getTime())) return "";
  const pad = (n: number) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}
function fromDatetimeLocal(value: string): number {
  const ms = new Date(value).getTime();
  return Number.isNaN(ms) ? 0 : Math.floor(ms / 1000);
}

export function defaultSchedule(kind: ScheduleKind, now: number = Date.now()): EngagementSchedule {
  const nowSec = Math.floor(now / 1000);
  switch (kind) {
    case "one-shot":
      return { kind: "one-shot", atEpochSec: nowSec + 3600 };
    case "interval":
      return { kind: "interval", everySec: 3600, anchorEpochSec: nowSec };
    case "daily":
      return { kind: "daily", secondOfDayUtc: 12 * 3600 };
    case "weekly":
      return { kind: "weekly", dayOfWeekUtc: 0, secondOfDayUtc: 12 * 3600 };
  }
}

// ── Schedule editor (one-shot / interval / daily / weekly + next-fire preview) ─
export function ScheduleEditor({
  value,
  onChange,
  now = Date.now(),
}: {
  value: EngagementSchedule;
  onChange: (next: EngagementSchedule) => void;
  now?: number;
}): JSX.Element {
  return (
    <div className="ob-builder" style={{ gap: "0.5rem" }}>
      <LabeledField label="Schedule kind" hint="A small, safe set — never an arbitrary cron expression.">
        <select
          className="field__input"
          value={value.kind}
          onChange={(e) => onChange(defaultSchedule(e.target.value as ScheduleKind, now))}
          aria-label="Schedule kind"
        >
          {SCHEDULE_KINDS.map((k) => (
            <option key={k} value={k}>
              {k}
            </option>
          ))}
        </select>
      </LabeledField>

      {value.kind === "one-shot" ? (
        <LabeledField label="Fire at" hint="Local time; sent as a UTC epoch second.">
          <input
            className="field__input"
            type="datetime-local"
            value={toDatetimeLocal(value.atEpochSec)}
            onChange={(e) => onChange({ kind: "one-shot", atEpochSec: fromDatetimeLocal(e.target.value) })}
            aria-label="One-shot fire time"
          />
        </LabeledField>
      ) : null}

      {value.kind === "interval" ? (
        <div className="card-grid">
          <LabeledField label="Every (seconds)" hint="Must be a positive integer.">
            <input
              className="field__input"
              type="number"
              min={1}
              value={value.everySec}
              onChange={(e) => onChange({ ...value, everySec: Math.max(1, Math.floor(Number(e.target.value) || 0)) })}
              aria-label="Interval seconds"
            />
          </LabeledField>
          <LabeledField label="Anchor" hint="The interval is measured from this instant.">
            <input
              className="field__input"
              type="datetime-local"
              value={toDatetimeLocal(value.anchorEpochSec)}
              onChange={(e) => onChange({ ...value, anchorEpochSec: fromDatetimeLocal(e.target.value) })}
              aria-label="Interval anchor time"
            />
          </LabeledField>
        </div>
      ) : null}

      {value.kind === "daily" ? (
        <LabeledField label="Time of day (UTC)" hint="Fires once each day at this UTC time.">
          <input
            className="field__input"
            type="time"
            value={secondsToTime(value.secondOfDayUtc)}
            onChange={(e) => onChange({ kind: "daily", secondOfDayUtc: timeToSeconds(e.target.value) })}
            aria-label="Daily time of day UTC"
          />
        </LabeledField>
      ) : null}

      {value.kind === "weekly" ? (
        <div className="card-grid">
          <LabeledField label="Day of week (0–6, UTC)" hint="Index within the UTC week window (0 = the anchor day, Thursday).">
            <input
              className="field__input"
              type="number"
              min={0}
              max={6}
              value={value.dayOfWeekUtc}
              onChange={(e) => onChange({ ...value, dayOfWeekUtc: Math.min(6, Math.max(0, Math.floor(Number(e.target.value) || 0))) })}
              aria-label="Weekly day of week"
            />
          </LabeledField>
          <LabeledField label="Time of day (UTC)">
            <input
              className="field__input"
              type="time"
              value={secondsToTime(value.secondOfDayUtc)}
              onChange={(e) => onChange({ ...value, secondOfDayUtc: timeToSeconds(e.target.value) })}
              aria-label="Weekly time of day UTC"
            />
          </LabeledField>
        </div>
      ) : null}

      <div className="world-panel world-panel--proposed" role="status">
        <StatusIndicator tone="neutral" label={describeSchedule(value)} />
        <p className="muted" style={{ marginTop: "0.25rem" }}>
          {nextFireLabel(value, now)}
        </p>
      </div>
    </div>
  );
}

// ── Engagement config builder (module toggles + validate + Phase-4 hand-off) ──
export function emptyEngagementModules(): EngagementModules {
  return {};
}

export interface EngagementModuleToggles {
  tickets: boolean;
  suggestions: boolean;
  polls: boolean;
  announcements: boolean;
  events: boolean;
  statistics: boolean;
}

/** Structural validation of an engagement config draft (the in-browser subset). */
export function validateEngagementDraft(modules: EngagementModules): ContentFinding[] {
  const findings: ContentFinding[] = [];
  if (Object.keys(modules).length === 0) {
    findings.push({ severity: "warning", message: "No engagement module is enabled yet — enable at least one to configure." });
  }
  const t = modules.tickets;
  if (t) {
    if (t.maxOpenPerUser < 1 || t.maxOpenPerUser > 20) findings.push({ severity: "blocker", message: "Tickets: max open per user must be between 1 and 20." });
    if (t.cooldownSeconds < 0 || t.cooldownSeconds > 86_400) findings.push({ severity: "blocker", message: "Tickets: cooldown must be between 0 and 86400 seconds." });
  }
  const s = modules.statistics;
  if (s && (s.refreshIntervalSeconds < 300 || s.refreshIntervalSeconds > 86_400)) {
    findings.push({ severity: "blocker", message: "Statistics: refresh interval must be between 300 and 86400 seconds." });
  }
  const items = modules.announcements?.items ?? [];
  const ids = new Set<string>();
  for (const item of items) {
    if (!item.id.trim()) findings.push({ severity: "blocker", message: "Every scheduled announcement needs a stable id." });
    else if (ids.has(item.id)) findings.push({ severity: "blocker", message: `Duplicate announcement id "${item.id}".` });
    if (item.id) ids.add(item.id);
    if (!item.channelKey.trim()) findings.push({ severity: "blocker", message: `Announcement "${item.id || "(unnamed)"}" needs a channel resource key.` });
  }
  return findings;
}

function ModuleToggle({ label, enabled, onToggle, children }: { label: string; enabled: boolean; onToggle: (v: boolean) => void; children?: ReactNode }): JSX.Element {
  return (
    <Card title={label}>
      <label className="row" style={{ gap: "0.5rem", alignItems: "center" }}>
        <input type="checkbox" checked={enabled} onChange={(e) => onToggle(e.target.checked)} aria-label={`Enable ${label}`} />
        <span>Enable {label}</span>
      </label>
      {enabled ? <div style={{ marginTop: "0.75rem" }}>{children}</div> : null}
    </Card>
  );
}

function csv(list: string[]): string {
  return list.join(", ");
}
function parseCsv(value: string): string[] {
  return value.split(",").map((v) => v.trim()).filter(Boolean);
}

export function EngagementConfigBuilder({
  initial,
  onValidateRemote,
  onGenerateDraft,
  message,
  busy = false,
  releaseReviewHref = "/app/release-review",
}: {
  initial?: EngagementModules;
  onValidateRemote?: (modules: EngagementModules) => void;
  onGenerateDraft?: (modules: EngagementModules) => void;
  message?: string | null;
  busy?: boolean;
  releaseReviewHref?: string;
}): JSX.Element {
  const [modules, setModules] = useState<EngagementModules>(initial ?? emptyEngagementModules());

  const findings = validateEngagementDraft(modules);
  const blockers = findings.filter((f) => f.severity === "blocker");
  const anyModule = Object.keys(modules).length > 0;
  const valid = blockers.length === 0 && anyModule;

  const patch = (p: Partial<EngagementModules>) => setModules((m) => ({ ...m, ...p }));
  const removeKey = (key: keyof EngagementModules) =>
    setModules((m) => {
      const next = { ...m };
      delete next[key];
      return next;
    });

  const toggleTickets = (on: boolean) => (on ? patch({ tickets: { enabled: true, schemaVersion: "1", supportRoleKeys: [], maxOpenPerUser: 2, cooldownSeconds: 60 } }) : removeKey("tickets"));
  const toggleSuggestions = (on: boolean) => (on ? patch({ suggestions: { enabled: true, schemaVersion: "1", allowDownvotes: true, reviewRoleKeys: [] } }) : removeKey("suggestions"));
  const togglePolls = (on: boolean) => (on ? patch({ polls: { enabled: true, schemaVersion: "1", allowMultipleChoice: false } }) : removeKey("polls"));
  const toggleEvents = (on: boolean) => (on ? patch({ events: { enabled: true, schemaVersion: "1", allowRsvp: true } }) : removeKey("events"));
  const toggleStatistics = (on: boolean) => (on ? patch({ statistics: { enabled: true, schemaVersion: "1", refreshIntervalSeconds: 3600 } }) : removeKey("statistics"));
  const toggleAnnouncements = (on: boolean) => (on ? patch({ announcements: { enabled: true, schemaVersion: "1", items: [] } }) : removeKey("announcements"));

  const addAnnouncement = () =>
    setModules((m) => ({
      ...m,
      announcements: {
        enabled: true,
        schemaVersion: "1",
        items: [
          ...(m.announcements?.items ?? []),
          { id: `announcement-${(m.announcements?.items.length ?? 0) + 1}`, channelKey: "", message: { plainText: "" }, schedule: defaultSchedule("daily"), enabled: true },
        ],
      },
    }));

  return (
    <div className="ob-builder">
      <div className="dryrun-banner" role="note">
        <strong>{NO_DISCORD_CHANGES}</strong>
        <span>Enable and configure engagement modules, validate the draft, then hand off to the Phase 4 approval flow. There is no bypass deploy button here.</span>
      </div>

      <ModuleToggle label="Tickets" enabled={Boolean(modules.tickets)} onToggle={toggleTickets}>
        {modules.tickets ? (
          <div className="card-grid">
            <LabeledField label="Panel channel key" hint="Where the ticket panel lives (e.g. channel.support).">
              <input className="field__input" value={modules.tickets.panelChannelKey ?? ""} onChange={(e) => patch({ tickets: { ...modules.tickets!, panelChannelKey: e.target.value || undefined } })} aria-label="Ticket panel channel key" />
            </LabeledField>
            <LabeledField label="Ticket category key" hint="Category new ticket channels are created under.">
              <input className="field__input" value={modules.tickets.ticketCategoryKey ?? ""} onChange={(e) => patch({ tickets: { ...modules.tickets!, ticketCategoryKey: e.target.value || undefined } })} aria-label="Ticket category key" />
            </LabeledField>
            <LabeledField label="Support role keys (comma-separated)">
              <input className="field__input" value={csv(modules.tickets.supportRoleKeys)} onChange={(e) => patch({ tickets: { ...modules.tickets!, supportRoleKeys: parseCsv(e.target.value) } })} aria-label="Support role keys" />
            </LabeledField>
            <LabeledField label="Max open per user" hint="1–20.">
              <input className="field__input" type="number" min={1} max={20} value={modules.tickets.maxOpenPerUser} onChange={(e) => patch({ tickets: { ...modules.tickets!, maxOpenPerUser: Number(e.target.value) } })} aria-label="Max open per user" />
            </LabeledField>
            <LabeledField label="Cooldown (seconds)" hint="0–86400.">
              <input className="field__input" type="number" min={0} max={86_400} value={modules.tickets.cooldownSeconds} onChange={(e) => patch({ tickets: { ...modules.tickets!, cooldownSeconds: Number(e.target.value) } })} aria-label="Ticket cooldown seconds" />
            </LabeledField>
          </div>
        ) : null}
      </ModuleToggle>

      <ModuleToggle label="Suggestions" enabled={Boolean(modules.suggestions)} onToggle={toggleSuggestions}>
        {modules.suggestions ? (
          <div className="card-grid">
            <LabeledField label="Suggestions channel key">
              <input className="field__input" value={modules.suggestions.channelKey ?? ""} onChange={(e) => patch({ suggestions: { ...modules.suggestions!, channelKey: e.target.value || undefined } })} aria-label="Suggestions channel key" />
            </LabeledField>
            <LabeledField label="Review role keys (comma-separated)">
              <input className="field__input" value={csv(modules.suggestions.reviewRoleKeys)} onChange={(e) => patch({ suggestions: { ...modules.suggestions!, reviewRoleKeys: parseCsv(e.target.value) } })} aria-label="Suggestion review role keys" />
            </LabeledField>
            <label className="row" style={{ gap: "0.5rem", alignItems: "center" }}>
              <input type="checkbox" checked={modules.suggestions.allowDownvotes} onChange={(e) => patch({ suggestions: { ...modules.suggestions!, allowDownvotes: e.target.checked } })} aria-label="Allow downvotes" />
              <span>Allow downvotes</span>
            </label>
          </div>
        ) : null}
      </ModuleToggle>

      <ModuleToggle label="Polls" enabled={Boolean(modules.polls)} onToggle={togglePolls}>
        {modules.polls ? (
          <div className="card-grid">
            <LabeledField label="Polls channel key">
              <input className="field__input" value={modules.polls.channelKey ?? ""} onChange={(e) => patch({ polls: { ...modules.polls!, channelKey: e.target.value || undefined } })} aria-label="Polls channel key" />
            </LabeledField>
            <label className="row" style={{ gap: "0.5rem", alignItems: "center" }}>
              <input type="checkbox" checked={modules.polls.allowMultipleChoice} onChange={(e) => patch({ polls: { ...modules.polls!, allowMultipleChoice: e.target.checked } })} aria-label="Allow multiple choice" />
              <span>Allow multiple-choice polls</span>
            </label>
          </div>
        ) : null}
      </ModuleToggle>

      <ModuleToggle label="Events" enabled={Boolean(modules.events)} onToggle={toggleEvents}>
        {modules.events ? (
          <div className="card-grid">
            <LabeledField label="Announce channel key">
              <input className="field__input" value={modules.events.announceChannelKey ?? ""} onChange={(e) => patch({ events: { ...modules.events!, announceChannelKey: e.target.value || undefined } })} aria-label="Events announce channel key" />
            </LabeledField>
            <label className="row" style={{ gap: "0.5rem", alignItems: "center" }}>
              <input type="checkbox" checked={modules.events.allowRsvp} onChange={(e) => patch({ events: { ...modules.events!, allowRsvp: e.target.checked } })} aria-label="Allow RSVP" />
              <span>Allow RSVP</span>
            </label>
          </div>
        ) : null}
      </ModuleToggle>

      <ModuleToggle label="Statistics" enabled={Boolean(modules.statistics)} onToggle={toggleStatistics}>
        {modules.statistics ? (
          <div className="card-grid">
            <LabeledField label="Member-count channel key" hint="ODIN updates this channel's name with the count (managed).">
              <input className="field__input" value={modules.statistics.memberCountChannelKey ?? ""} onChange={(e) => patch({ statistics: { ...modules.statistics!, memberCountChannelKey: e.target.value || undefined } })} aria-label="Member count channel key" />
            </LabeledField>
            <LabeledField label="Refresh interval (seconds)" hint="300–86400.">
              <input className="field__input" type="number" min={300} max={86_400} value={modules.statistics.refreshIntervalSeconds} onChange={(e) => patch({ statistics: { ...modules.statistics!, refreshIntervalSeconds: Number(e.target.value) } })} aria-label="Statistics refresh interval seconds" />
            </LabeledField>
          </div>
        ) : null}
      </ModuleToggle>

      <ModuleToggle label="Scheduled announcements" enabled={Boolean(modules.announcements)} onToggle={toggleAnnouncements}>
        {modules.announcements ? (
          <div className="ob-builder">
            {modules.announcements.items.map((item, i) => (
              <div key={i} className="world-panel world-panel--proposed" style={{ marginBottom: "0.75rem" }}>
                <div className="card-grid">
                  <LabeledField label="Announcement id">
                    <input className="field__input" value={item.id} onChange={(e) => patch({ announcements: { ...modules.announcements!, items: modules.announcements!.items.map((it, idx) => (idx === i ? { ...it, id: e.target.value } : it)) } })} aria-label={`Announcement ${i + 1} id`} />
                  </LabeledField>
                  <LabeledField label="Channel key">
                    <input className="field__input" value={item.channelKey} onChange={(e) => patch({ announcements: { ...modules.announcements!, items: modules.announcements!.items.map((it, idx) => (idx === i ? { ...it, channelKey: e.target.value } : it)) } })} aria-label={`Announcement ${i + 1} channel key`} />
                  </LabeledField>
                </div>
                <LabeledField label="Message text">
                  <textarea className="field__input" rows={2} value={item.message.plainText ?? ""} onChange={(e) => patch({ announcements: { ...modules.announcements!, items: modules.announcements!.items.map((it, idx) => (idx === i ? { ...it, message: { ...it.message, plainText: e.target.value } } : it)) } })} aria-label={`Announcement ${i + 1} message`} />
                </LabeledField>
                <ScheduleEditor value={item.schedule} onChange={(sch) => patch({ announcements: { ...modules.announcements!, items: modules.announcements!.items.map((it, idx) => (idx === i ? { ...it, schedule: sch } : it)) } })} />
                <button type="button" className="btn btn--ghost" onClick={() => patch({ announcements: { ...modules.announcements!, items: modules.announcements!.items.filter((_, idx) => idx !== i) } })} aria-label={`Remove announcement ${i + 1}`} style={{ marginTop: "0.5rem" }}>
                  Remove
                </button>
              </div>
            ))}
            <button type="button" className="btn btn--secondary" onClick={addAnnouncement}>
              Add scheduled announcement
            </button>
          </div>
        ) : null}
      </ModuleToggle>

      <Card title="Validation">
        <StatusIndicator tone={valid ? "ok" : "danger"} label={valid ? "Structurally valid" : anyModule ? `${blockers.length} blocker(s)` : "No module enabled"} />
        <FindingList findings={findings} emptyLabel="No structural problems found." />
        <p className="muted" style={{ marginTop: "0.5rem" }}>
          Structural checks run here in the browser. Full validation (unresolved resource keys, duplicate ids) runs server-side when you generate a draft. {NO_DISCORD_CHANGES}
        </p>
      </Card>

      {message ? (
        <div className="world-panel world-panel--current" role="status">
          <p>{message}</p>
        </div>
      ) : null}

      <div className="row" style={{ gap: "0.5rem", flexWrap: "wrap" }}>
        <button type="button" className="btn btn--secondary" disabled={busy || !valid || !onGenerateDraft} onClick={() => onGenerateDraft?.(modules)}>
          Generate draft &amp; validate
        </button>
        <button type="button" className="btn btn--secondary" disabled={busy || !valid || !onValidateRemote} onClick={() => onValidateRemote?.(modules)}>
          Validate against server
        </button>
      </div>

      <Card title="Hand off to the Phase 4 approval flow">
        <p>
          ODIN does not deploy engagement config from this builder. A validated draft joins the same controlled-deployment pipeline and travels the
          existing dry-run → approval → queue path. {HUMAN_APPROVAL_REQUIRED}
        </p>
        <a className="btn btn--primary" href={releaseReviewHref}>
          Open Phase 4 release review →
        </a>
        <p className="muted" style={{ marginTop: "0.75rem" }}>
          There is deliberately no "deploy" or "activate now" button here. Activation only links a draft to a deployment that already cleared Phase 4 review and approval.
        </p>
      </Card>
    </div>
  );
}

// ── Ticket views ──────────────────────────────────────────────────────────────
export function ticketStatusTone(status: TicketStatus): StatusTone {
  switch (status) {
    case "OPEN":
      return "warn";
    case "CLAIMED":
    case "AWAITING_USER":
      return "neutral";
    case "RESOLVED":
      return "ok";
    case "CLOSED":
    case "ARCHIVED":
      return "neutral";
    default:
      return "neutral";
  }
}

function fmtDate(iso: string | null): string {
  if (!iso) return "—";
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? iso : formatDateTime(d);
}

export function TicketList({ tickets, selectedId, onSelect }: { tickets: TicketRow[]; selectedId?: string | null; onSelect?: (id: string) => void }): JSX.Element {
  if (tickets.length === 0) {
    return (
      <Card title="Open tickets">
        <p className="muted">No tickets recorded for this server yet.</p>
      </Card>
    );
  }
  return (
    <Card title={`Tickets (${tickets.length})`} description="Real records from ODIN's ticket store. Selecting a ticket shows its detail and staff actions.">
      <div className="op-list">
        {tickets.map((t) => (
          <button
            key={t.id}
            type="button"
            className={`list-row${selectedId === t.id ? "" : " list-row--muted"}`}
            style={{ cursor: "pointer", textAlign: "left" }}
            aria-pressed={selectedId === t.id}
            onClick={() => onSelect?.(t.id)}
          >
            <StatusIndicator tone={ticketStatusTone(t.status)} label={t.status} />
            <span>
              <strong>{t.category}</strong> {t.subject ? `· ${t.subject.slice(0, 40)}` : ""}
            </span>
            <span className="muted">
              requester {t.requesterDiscordUserId} · opened {fmtDate(t.openedAt)}
            </span>
          </button>
        ))}
      </div>
    </Card>
  );
}

export function TicketDetail({
  ticket,
  onClaim,
  onClose,
  onArchive,
  busy = false,
  message,
}: {
  ticket: TicketRow;
  onClaim?: (id: string) => void;
  onClose?: (id: string) => void;
  onArchive?: (id: string) => void;
  busy?: boolean;
  message?: string | null;
}): JSX.Element {
  const canClaim = ticket.status === "OPEN";
  const canClose = ticket.status === "OPEN" || ticket.status === "CLAIMED" || ticket.status === "AWAITING_USER" || ticket.status === "RESOLVED";
  const canArchive = ticket.status === "CLOSED";
  return (
    <Card title={`Ticket · ${ticket.category}`} description="Staff actions update ODIN records. Channel creation runs through the locked pipeline — no live Discord effect by default.">
      <div className="kv-grid">
        <div className="kv">
          <span className="kv__k">Status</span>
          <span className="kv__v">
            <StatusIndicator tone={ticketStatusTone(ticket.status)} label={ticket.status} />
          </span>
        </div>
        <div className="kv">
          <span className="kv__k">Requester</span>
          <span className="kv__v kv__v--mono">{ticket.requesterDiscordUserId}</span>
        </div>
        <div className="kv">
          <span className="kv__k">Subject</span>
          <span className="kv__v">{ticket.subject ?? "— none —"}</span>
        </div>
        <div className="kv">
          <span className="kv__k">Discord channel</span>
          <span className="kv__v kv__v--mono">{ticket.discordChannelId ?? "— not created (locked pipeline) —"}</span>
        </div>
        <div className="kv">
          <span className="kv__k">Opened</span>
          <span className="kv__v">{fmtDate(ticket.openedAt)}</span>
        </div>
        <div className="kv">
          <span className="kv__k">Closed</span>
          <span className="kv__v">{fmtDate(ticket.closedAt)}</span>
        </div>
      </div>

      {message ? (
        <div className="world-panel world-panel--current" role="status" style={{ marginTop: "0.75rem" }}>
          <p>{message}</p>
        </div>
      ) : null}

      <div className="row" style={{ gap: "0.5rem", marginTop: "0.75rem", flexWrap: "wrap" }}>
        <button type="button" className="btn btn--secondary" disabled={busy || !onClaim || !canClaim} onClick={() => onClaim?.(ticket.id)}>
          Claim
        </button>
        <button type="button" className="btn btn--secondary" disabled={busy || !onClose || !canClose} onClick={() => onClose?.(ticket.id)}>
          Close
        </button>
        <button type="button" className="btn btn--secondary" disabled={busy || !onArchive || !canArchive} onClick={() => onArchive?.(ticket.id)}>
          Archive
        </button>
      </div>
      <p className="muted" style={{ marginTop: "0.5rem" }}>
        {LOCKED_TICKET_NOTE} There is deliberately no "open a ticket for this user" button that acts live.
      </p>
    </Card>
  );
}

// ── Suggestions (list with live tallies + close) ──────────────────────────────
export function suggestionStatusTone(status: SuggestionStatus): StatusTone {
  switch (status) {
    case "OPEN":
      return "neutral";
    case "APPROVED":
    case "IMPLEMENTED":
      return "ok";
    case "REJECTED":
      return "danger";
    case "CLOSED":
      return "neutral";
    default:
      return "neutral";
  }
}

export function SuggestionList({ suggestions, onClose, busy = false }: { suggestions: SuggestionWithTally[]; onClose?: (id: string, status: SuggestionStatus) => void; busy?: boolean }): JSX.Element {
  if (suggestions.length === 0) {
    return (
      <Card title="Suggestions">
        <p className="muted">No suggestions recorded for this server yet.</p>
      </Card>
    );
  }
  return (
    <Card title={`Suggestions (${suggestions.length})`} description="Live up/down tallies are computed from recorded member votes — real database values.">
      <div className="op-list">
        {suggestions.map(({ suggestion, tally }) => (
          <div key={suggestion.id} className="list-row list-row--muted" style={{ cursor: "default", flexWrap: "wrap" }}>
            <StatusIndicator tone={suggestionStatusTone(suggestion.status)} label={suggestion.status} />
            <span style={{ flex: 1 }}>{suggestion.content.slice(0, 100)}</span>
            <span className="row" style={{ gap: "0.4rem" }} aria-label={`Tally for suggestion ${suggestion.id}`}>
              <span className="count-chip count-chip--ok"><strong>{tally.up}</strong> up</span>
              <span className="count-chip count-chip--danger"><strong>{tally.down}</strong> down</span>
              <span className="count-chip count-chip--neutral"><strong>{tally.score}</strong> score</span>
            </span>
            {suggestion.status === "OPEN" ? (
              <select
                className="field__input"
                defaultValue=""
                aria-label={`Close suggestion ${suggestion.id}`}
                disabled={busy || !onClose}
                onChange={(e) => {
                  if (e.target.value) onClose?.(suggestion.id, e.target.value as SuggestionStatus);
                }}
                style={{ minWidth: 140 }}
              >
                <option value="" disabled>
                  Decide…
                </option>
                {SUGGESTION_CLOSE_STATUSES.map((s) => (
                  <option key={s} value={s}>
                    {s}
                  </option>
                ))}
              </select>
            ) : null}
          </div>
        ))}
      </div>
    </Card>
  );
}

// ── Poll builder + list (live tallies) ────────────────────────────────────────
export interface PollDraft {
  question: string;
  options: PollOption[];
  allowMultiple: boolean;
  closesAt: string;
}

export function emptyPollDraft(): PollDraft {
  return { question: "", options: [{ id: "opt-1", label: "" }, { id: "opt-2", label: "" }], allowMultiple: false, closesAt: "" };
}

/** Pure validation for a poll draft. Returns human-readable blockers. */
export function validatePollDraft(draft: PollDraft): string[] {
  const errors: string[] = [];
  if (!draft.question.trim()) errors.push("A poll needs a question.");
  if (draft.question.length > 300) errors.push("The question may not exceed 300 characters.");
  const real = draft.options.filter((o) => o.label.trim());
  if (real.length < 2) errors.push("A poll needs at least two options with labels.");
  const ids = new Set<string>();
  for (const o of draft.options) {
    if (!o.id.trim()) errors.push("Every option needs a stable id.");
    else if (ids.has(o.id)) errors.push(`Duplicate option id "${o.id}".`);
    if (o.id) ids.add(o.id);
  }
  return errors;
}

export function PollBuilder({ onCreate, busy = false, result }: { onCreate?: (draft: PollDraft) => void; busy?: boolean; result?: string | null }): JSX.Element {
  const [draft, setDraft] = useState<PollDraft>(emptyPollDraft());
  const errors = validatePollDraft(draft);
  const canSubmit = errors.length === 0 && !busy && Boolean(onCreate);

  const patch = (p: Partial<PollDraft>) => setDraft((d) => ({ ...d, ...p }));
  const patchOption = (i: number, p: Partial<PollOption>) => setDraft((d) => ({ ...d, options: d.options.map((o, idx) => (idx === i ? { ...o, ...p } : o)) }));
  const addOption = () => setDraft((d) => ({ ...d, options: [...d.options, { id: `opt-${d.options.length + 1}`, label: "" }] }));
  const removeOption = (i: number) => setDraft((d) => ({ ...d, options: d.options.filter((_, idx) => idx !== i) }));

  return (
    <Card title="Create a poll">
      <div className="dryrun-banner" role="note">
        <strong>Locked pipeline.</strong>
        <span>Creating a poll records it and requests the poll message through the locked engagement boundary — no live Discord post by default. Vote tallies are real database values.</span>
      </div>

      <LabeledField label="Question">
        <input className="field__input" value={draft.question} onChange={(e) => patch({ question: e.target.value })} aria-label="Poll question" />
      </LabeledField>

      <Card title={`Options (${draft.options.length})`}>
        {draft.options.map((o, i) => (
          <div key={i} className="ob-option-row">
            <input className="field__input" placeholder="Option id" value={o.id} onChange={(e) => patchOption(i, { id: e.target.value })} aria-label={`Option ${i + 1} id`} style={{ minWidth: 120 }} />
            <input className="field__input" placeholder="Label" value={o.label} onChange={(e) => patchOption(i, { label: e.target.value })} aria-label={`Option ${i + 1} label`} />
            <button type="button" className="btn btn--ghost" onClick={() => removeOption(i)} aria-label={`Remove option ${i + 1}`}>
              Remove
            </button>
          </div>
        ))}
        <button type="button" className="btn btn--secondary" onClick={addOption} style={{ marginTop: "0.5rem" }}>
          Add option
        </button>
      </Card>

      <div className="card-grid">
        <label className="row" style={{ gap: "0.5rem", alignItems: "center" }}>
          <input type="checkbox" checked={draft.allowMultiple} onChange={(e) => patch({ allowMultiple: e.target.checked })} aria-label="Allow multiple choices" />
          <span>Allow multiple choices</span>
        </label>
        <LabeledField label="Closes at (optional)" hint="Leave empty for a poll with no automatic close.">
          <input className="field__input" type="datetime-local" value={draft.closesAt} onChange={(e) => patch({ closesAt: e.target.value })} aria-label="Poll closes at" />
        </LabeledField>
      </div>

      {errors.length > 0 ? (
        <ul className="ob-findings" aria-label="Poll blockers">
          {errors.map((e, i) => (
            <li key={i} className="ob-finding ob-finding--danger">
              <span className="sev sev--danger">BLOCKER</span> {e}
            </li>
          ))}
        </ul>
      ) : null}

      <button type="button" className="btn btn--primary" disabled={!canSubmit} onClick={() => onCreate?.(draft)} style={{ marginTop: "0.5rem" }}>
        {busy ? "Creating…" : "Create poll"}
      </button>

      {result ? (
        <div className="world-panel world-panel--proposed" role="status" style={{ marginTop: "1rem" }}>
          <p>{result}</p>
        </div>
      ) : null}
    </Card>
  );
}

export function PollList({ polls, onClose, busy = false }: { polls: PollWithTally[]; onClose?: (id: string) => void; busy?: boolean }): JSX.Element {
  if (polls.length === 0) {
    return (
      <Card title="Polls">
        <p className="muted">No polls recorded for this server yet.</p>
      </Card>
    );
  }
  return (
    <Card title={`Polls (${polls.length})`} description="Per-option counts are computed from recorded member votes — real database values.">
      <div className="op-list">
        {polls.map(({ poll, tally }) => (
          <div key={poll.id} className="world-panel world-panel--current" style={{ marginBottom: "0.75rem" }}>
            <div className="row" style={{ justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: "0.5rem" }}>
              <span>
                <StatusIndicator tone={poll.status === "OPEN" ? "warn" : "neutral"} label={poll.status} /> <strong>{poll.question}</strong>
              </span>
              <span className="muted">{tally.totalVoters} voter(s)</span>
            </div>
            <div className="op-list" style={{ marginTop: "0.5rem" }} aria-label={`Results for poll ${poll.id}`}>
              {poll.options.map((o) => (
                <div key={o.id} className="row" style={{ gap: "0.5rem", alignItems: "center" }}>
                  <span style={{ minWidth: 120 }}>{o.label}</span>
                  <span className="count-chip count-chip--neutral"><strong>{tally.counts[o.id] ?? 0}</strong> vote(s)</span>
                </div>
              ))}
            </div>
            {poll.status === "OPEN" ? (
              <button type="button" className="btn btn--secondary" disabled={busy || !onClose} onClick={() => onClose?.(poll.id)} style={{ marginTop: "0.5rem" }}>
                Close poll
              </button>
            ) : null}
          </div>
        ))}
      </div>
    </Card>
  );
}

// ── Scheduled content lists (announcements / reminders / events) ──────────────
export function ScheduledAnnouncementList({
  announcements,
  onToggle,
  onDelete,
  busy = false,
}: {
  announcements: AnnouncementRow[];
  onToggle?: (row: AnnouncementRow, enabled: boolean) => void;
  onDelete?: (row: AnnouncementRow) => void;
  busy?: boolean;
}): JSX.Element {
  if (announcements.length === 0) {
    return (
      <Card title="Scheduled announcements">
        <p className="muted">No scheduled announcements yet.</p>
      </Card>
    );
  }
  return (
    <Card title={`Scheduled announcements (${announcements.length})`}>
      <div className="op-list">
        {announcements.map((a) => (
          <div key={a.id} className="list-row list-row--muted" style={{ cursor: "default", flexWrap: "wrap" }}>
            <StatusIndicator tone={a.enabled ? "ok" : "neutral"} label={a.enabled ? "enabled" : "disabled"} />
            <span style={{ flex: 1 }}>
              <strong>{a.announcementKey}</strong> · {a.channelKey}
            </span>
            <span className="muted">{describeSchedule(a.schedule)} · {nextFireLabel(a.schedule)}</span>
            <button type="button" className="btn btn--ghost" disabled={busy || !onToggle} onClick={() => onToggle?.(a, !a.enabled)} aria-label={`${a.enabled ? "Disable" : "Enable"} announcement ${a.announcementKey}`}>
              {a.enabled ? "Disable" : "Enable"}
            </button>
            <button type="button" className="btn btn--ghost" disabled={busy || !onDelete} onClick={() => onDelete?.(a)} aria-label={`Delete announcement ${a.announcementKey}`}>
              Delete
            </button>
          </div>
        ))}
      </div>
    </Card>
  );
}

export function ReminderRowList({ reminders, onDelete, busy = false }: { reminders: ReminderRow[]; onDelete?: (row: ReminderRow) => void; busy?: boolean }): JSX.Element {
  if (reminders.length === 0) {
    return (
      <Card title="Reminders">
        <p className="muted">No reminders yet.</p>
      </Card>
    );
  }
  return (
    <Card title={`Reminders (${reminders.length})`} description="Reminders are create-and-delete — to change a schedule, delete and recreate it.">
      <div className="op-list">
        {reminders.map((r) => (
          <div key={r.id} className="list-row list-row--muted" style={{ cursor: "default", flexWrap: "wrap" }}>
            <StatusIndicator tone={r.enabled ? "ok" : "neutral"} label={r.enabled ? "enabled" : "disabled"} />
            <span style={{ flex: 1 }}>{r.content.slice(0, 80)}</span>
            <span className="muted">{r.channelKey ? `channel ${r.channelKey}` : r.discordUserId ? `DM ${r.discordUserId}` : "—"} · {describeSchedule(r.schedule)} · {nextFireLabel(r.schedule)}</span>
            <button type="button" className="btn btn--ghost" disabled={busy || !onDelete} onClick={() => onDelete?.(r)} aria-label={`Delete reminder ${r.id}`}>
              Delete
            </button>
          </div>
        ))}
      </div>
    </Card>
  );
}

export function eventStatusTone(status: EventRow["status"]): StatusTone {
  switch (status) {
    case "SCHEDULED":
      return "ok";
    case "CANCELLED":
      return "danger";
    case "COMPLETED":
      return "neutral";
    default:
      return "neutral";
  }
}

export function EventRowList({ events, onSetStatus, busy = false }: { events: EventRow[]; onSetStatus?: (row: EventRow, status: "CANCELLED" | "COMPLETED") => void; busy?: boolean }): JSX.Element {
  if (events.length === 0) {
    return (
      <Card title="Community events">
        <p className="muted">No events yet.</p>
      </Card>
    );
  }
  return (
    <Card title={`Community events (${events.length})`}>
      <div className="op-list">
        {events.map((e) => (
          <div key={e.id} className="list-row list-row--muted" style={{ cursor: "default", flexWrap: "wrap" }}>
            <StatusIndicator tone={eventStatusTone(e.status)} label={e.status} />
            <span style={{ flex: 1 }}>
              <strong>{e.title}</strong>
            </span>
            <span className="muted">starts {fmtDate(e.startsAt)}{e.announceChannelKey ? ` · announce ${e.announceChannelKey}` : ""}</span>
            {e.status === "SCHEDULED" ? (
              <>
                <button type="button" className="btn btn--ghost" disabled={busy || !onSetStatus} onClick={() => onSetStatus?.(e, "COMPLETED")} aria-label={`Complete event ${e.title}`}>
                  Complete
                </button>
                <button type="button" className="btn btn--ghost" disabled={busy || !onSetStatus} onClick={() => onSetStatus?.(e, "CANCELLED")} aria-label={`Cancel event ${e.title}`}>
                  Cancel
                </button>
              </>
            ) : null}
          </div>
        ))}
      </div>
    </Card>
  );
}

// ── Statistics view (real DB counts only) ─────────────────────────────────────
export function StatisticsView({ stats }: { stats: EngagementStatistics }): JSX.Element {
  const suggestionEntries = Object.entries(stats.suggestions.byStatus);
  const pollEntries = Object.entries(stats.polls.byStatus);
  const ticketEntries = Object.entries(stats.tickets.byStatus);
  return (
    <div className="ob-ops">
      <div className="dryrun-banner" role="status">
        <strong>{NO_DISCORD_CHANGES}</strong>
        <span>{REAL_COUNTS_NOTE} This page never posts to, edits, or approves anything on Discord.</span>
      </div>

      <Card title="Tickets">
        <div className="row" style={{ flexWrap: "wrap", gap: "0.5rem" }} aria-label="Ticket counts">
          <span className="count-chip count-chip--warn"><strong>{stats.tickets.open}</strong> open</span>
          {ticketEntries.map(([status, count]) => (
            <span key={status} className="count-chip count-chip--neutral"><strong>{count}</strong> {status}</span>
          ))}
        </div>
      </Card>

      <Card title="Suggestions">
        <div className="row" style={{ flexWrap: "wrap", gap: "0.5rem" }} aria-label="Suggestion counts">
          <span className="count-chip count-chip--neutral"><strong>{stats.suggestions.total}</strong> total</span>
          {suggestionEntries.map(([status, count]) => (
            <span key={status} className="count-chip count-chip--neutral"><strong>{count}</strong> {status}</span>
          ))}
        </div>
      </Card>

      <Card title="Polls" description="Recent polls show per-option tallies from recorded votes.">
        <div className="row" style={{ flexWrap: "wrap", gap: "0.5rem" }} aria-label="Poll counts">
          {pollEntries.length === 0 ? <span className="muted">No polls recorded.</span> : null}
          {pollEntries.map(([status, count]) => (
            <span key={status} className="count-chip count-chip--neutral"><strong>{count}</strong> {status}</span>
          ))}
        </div>
        {stats.polls.recent.length > 0 ? (
          <div className="op-list" style={{ marginTop: "0.75rem" }}>
            {stats.polls.recent.map((p) => (
              <div key={p.id} className="list-row list-row--muted" style={{ cursor: "default", flexWrap: "wrap" }}>
                <StatusIndicator tone={p.status === "OPEN" ? "warn" : "neutral"} label={p.status} />
                <span style={{ flex: 1 }}>{p.question}</span>
                <span className="muted">{p.tally.totalVoters} voter(s)</span>
              </div>
            ))}
          </div>
        ) : null}
      </Card>

      <Card title="Recent server snapshots" description="Member/online counts captured by the worker — real recorded values.">
        {stats.recentSnapshots.length === 0 ? (
          <p className="muted">No snapshots recorded yet.</p>
        ) : (
          <div className="op-list">
            {stats.recentSnapshots.map((s) => (
              <div key={s.id} className="list-row list-row--muted" style={{ cursor: "default" }}>
                <span className="badge">{fmtDate(s.createdAt)}</span>
                <span>{s.memberCount ?? "—"} members</span>
                <span className="muted">{s.onlineCount ?? "—"} online</span>
              </div>
            ))}
          </div>
        )}
      </Card>

      <Card title="Engagement analytics">
        <p className="muted">
          To keep this honest, ODIN does not fabricate per-member engagement analytics. The numbers above are the real database counts, tallies and worker
          snapshots — nothing here is estimated or invented.
        </p>
      </Card>
    </div>
  );
}

// Re-export shared safety strings for the route to reuse.
export { NO_DISCORD_CHANGES, HUMAN_APPROVAL_REQUIRED } from "./onboarding";
